import {useState} from "react";
import {Dices, MousePointer2, Plus, X, Zap} from "lucide-react";
import {Button} from "@/components/ui/button.tsx";
import {useCombatants} from "@/encounter/combatants/CombatantHooks.ts";
import {rollInitiative, useTurn} from "@/encounter/turns/TurnHooks.ts";
import {useTableTopSelection} from "@/tabletop/selection/TableTopSelectionHooks.ts";
import TokenFramePreview from "@/tabletop/tokens/TokenFramePreview.tsx";
import {tokenFrameStyle} from "@/tabletop/tokens/TokenFrameStyles.ts";

type Draft = {initiative: string; surprised: boolean};
function initiative(value: string): number | undefined {
    if (!value.trim()) return;
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed >= -2147483648 && parsed <= 2147483647 ? parsed : undefined;
}

export default function CombatReinforcements({allLevels, levelId, onClose}: {allLevels: boolean; levelId?: string; onClose: () => void}) {
    const {combatants} = useCombatants();
    const {combatState, runCombatCommand} = useTurn();
    const {selectedCombatants} = useTableTopSelection();
    const [drafts, setDrafts] = useState<Record<string, Draft>>({});
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const members = new Set(combatState.participants.map(entry => entry.combatantId));
    const candidates = combatants.filter(actor => !members.has(actor.id) &&
        (allLevels || actor.levelId == null || actor.levelId === levelId || drafts[actor.id]));
    const chosen = combatants.filter(actor => drafts[actor.id] && !members.has(actor.id));
    const deleted = Object.keys(drafts).some(id => !combatants.some(actor => actor.id === id));
    const ready = chosen.filter(actor => initiative(drafts[actor.id].initiative) != null).length;
    const change = (id: string, patch: Partial<Draft>) => setDrafts(values => ({...values, [id]: {...values[id], initiative: values[id]?.initiative ?? "", surprised: values[id]?.surprised ?? false, ...patch}}));
    const submit = async () => {
        if (busy || !chosen.length || ready !== chosen.length || deleted) return;
        setBusy(true); setError("");
        try {
            await runCombatCommand({action: "ADD_PARTICIPANTS",
                participants: chosen.map(actor => ({combatantId: actor.id, surprised: drafts[actor.id].surprised})),
                initiatives: chosen.map(actor => ({combatantId: actor.id, initiative: initiative(drafts[actor.id].initiative)!}))});
            onClose();
        } catch {setError("Aufnahme fehlgeschlagen. Die Auswahl bleibt erhalten.");}
        finally {setBusy(false);}
    };
    return <section aria-label="Teilnehmer hinzufügen" className="space-y-2 rounded-lg border border-sky-400/30 p-2">
        <h3 className="text-sm font-semibold">Teilnehmer hinzufügen</h3>
        <p className="text-xs text-white/65">Neue Teilnehmer handeln ab der nächsten Runde.</p>
        <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" disabled={busy || !selectedCombatants.some(actor => !members.has(actor.id))}
                aria-label="Kartenauswahl für Verstärkungen übernehmen" onClick={() => setDrafts(current => {
                    const next = {...current};
                    selectedCombatants.filter(actor => !members.has(actor.id)).forEach(actor => {next[actor.id] ??= {initiative: "", surprised: false};});
                    return next;
                })}><MousePointer2 aria-hidden="true"/> Auswahl</Button>
            <Button size="sm" variant="secondary" disabled={busy || !chosen.some(actor => actor.hostile && !drafts[actor.id].initiative.trim())}
                aria-label="Fehlende Verstärkungs-Initiativen würfeln" onClick={() => {
                    const rolled = chosen.filter(actor => actor.hostile && !drafts[actor.id].initiative.trim())
                        .map(actor => ({id: actor.id, value: String(rollInitiative(actor))}));
                    setDrafts(current => {
                    const next = {...current};
                    rolled.forEach(value => {
                        next[value.id] = {...current[value.id], initiative: value.value};
                    });
                    return next;
                });}}><Dices aria-hidden="true"/> Gegner würfeln</Button>
        </div>
        {[false, true].map(hostile => <ul key={String(hostile)} className="space-y-1">
            {candidates.filter(actor => actor.hostile === hostile).map(actor => {
                const draft = drafts[actor.id];
                const invalid = draft?.initiative.trim() && initiative(draft.initiative) == null;
                return <li key={actor.id} className={`grid h-12 grid-cols-[auto_auto_minmax(0,1fr)_auto_auto_auto] items-center gap-1.5 rounded border px-1.5 ${hostile ? "border-red-500/20 bg-red-500/10" : "border-emerald-500/20 bg-emerald-500/10"}`}>
                    <input type="checkbox" aria-label={`${actor.name} hinzufügen`} checked={!!draft} disabled={busy} onChange={event => {
                        if (event.target.checked) change(actor.id, {});
                        else setDrafts(current => {const next = {...current}; delete next[actor.id]; return next;});
                    }}/>
                    <TokenFramePreview style={tokenFrameStyle(actor.tokenFrame)} image={actor.character.image} className="size-8"/>
                    <span className="truncate text-sm" title={actor.name}>{actor.name}</span>
                    <label className="flex items-center gap-1" title="Überraschung gilt nur in Runde 1 des Kampfes">
                        <input type="checkbox" aria-label={`${actor.name} beim Beitritt überrascht`} checked={draft?.surprised ?? false} disabled={busy || !draft}
                            onChange={event => change(actor.id, {surprised: event.target.checked})}/><Zap aria-hidden="true" className="size-3 text-amber-300"/>
                    </label>
                    <input aria-label={`Eintrittsinitiative für ${actor.name}`} aria-invalid={!!invalid} inputMode="numeric"
                        className="h-7 w-14 rounded border border-white/25 bg-black/25 px-1.5 text-sm aria-invalid:border-red-400"
                        value={draft?.initiative ?? ""} placeholder="–" disabled={busy || !draft}
                        onChange={event => change(actor.id, {initiative: event.target.value})}/>
                    <Button size="icon-sm" variant="secondary" aria-label={`Eintrittsinitiative für ${actor.name} würfeln`} disabled={busy || !draft}
                        onClick={() => change(actor.id, {initiative: String(rollInitiative(actor))})}><Dices aria-hidden="true"/></Button>
                </li>;
            })}
        </ul>)}
        {!candidates.length && <p className="text-xs text-white/60">Keine weiteren Tokens auf dieser Ebene.</p>}
        <p className="text-xs text-white/70">{ready} von {chosen.length} bereit · Ganze Initiativewerte erforderlich</p>
        {deleted && <p className="text-xs text-amber-300">Ein ausgewähltes Token wurde gelöscht.</p>}
        {deleted && <Button size="sm" variant="secondary" onClick={() => setDrafts(values => Object.fromEntries(Object.entries(values).filter(([id]) => combatants.some(actor => actor.id === id))))}>Auswahl bereinigen</Button>}
        {error && <p role="alert" className="text-xs text-red-300">{error}</p>}
        <div className="flex gap-2">
            <Button size="sm" disabled={busy || deleted || !chosen.length || ready !== chosen.length} aria-label="Teilnehmer in den Kampf aufnehmen" onClick={() => void submit()}><Plus aria-hidden="true"/> Aufnehmen</Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={onClose}><X aria-hidden="true"/> Abbrechen</Button>
        </div>
    </section>;
}
